import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { Option } from 'src/components/SelectBox';
import { primaryTimezoneOptions, timeOptions } from './constants';
import { formatTimeLabel } from 'src/utils/date';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  WorkflowTimezone,
  type WorkflowContactWindow,
} from 'src/interfaces/workflows.interface';
import { useNotification } from 'src/contexts/NotificationContext';
import { useLocation } from 'react-router-dom';

/**
 * Validates call times against legal restrictions.
 * See restrictions by state:
 * https://a-us.storyblok.com/f/1020997/x/d026cda979/state-calling-restrictions_april_2024.pdf
 */
const isOutsideLegalHours = (
  startTime: string, // "HH:MM" format
  endTime: string,
  selectedDays: number[], // 0=Sun, 1=Mon, etc.
): boolean => {
  if (!startTime || !endTime) return false;

  const startHour = parseInt(startTime.split(':')[0]);
  const [endHourStr, endMinuteStr] = endTime.split(':');
  const endHour = parseInt(endHourStr);
  const endMinutes = parseInt(endMinuteStr);

  // Check if earliest time is before 9 AM or latest time is after 8 PM
  const isEarlyViolation = startHour < 9;
  const isLateViolation = endHour > 20 || (endHour === 20 && endMinutes > 0);

  // Check for Sunday (value 0 in new system)
  const isSundayViolation = selectedDays.includes(0);

  return isEarlyViolation || isLateViolation || isSundayViolation;
};

type ContactWindowContextValue = {
  label: string;
  description: string;
  setLabel: (label: string) => void;
  setDescription: (description: string) => void;

  selectedDays: number[];
  toggleDay: (dayValue: number) => void;
  setSelectedDays: (days: number[]) => void;

  startTime: Option;
  endTime: Option;
  setStartTime: (option: Option) => void;
  setEndTime: (option: Option) => void;

  primaryTz: Option | null;
  setPrimaryTz: (option: Option | null) => void;

  useHolidays: boolean;
  setUseHolidays: (use: boolean) => void;

  blackoutDates: string[];
  setBlackoutDates: React.Dispatch<React.SetStateAction<string[]>>;

  scheduleWarningAccepted: boolean;
  setScheduleWarningAccepted: (accepted: boolean) => void;
};

const ContactWindowContext = createContext<
  ContactWindowContextValue | undefined
>(undefined);

type ContactWindowProviderProps = {
  children: React.ReactNode;
};

export const ContactWindowProvider: React.FC<ContactWindowProviderProps> = ({
  children,
}) => {
  const [label, setLabel] = useState<string>('Campaign schedule');
  const [description, setDescription] = useState<string>('');
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState<Option>(timeOptions[18]);
  const [endTime, setEndTime] = useState<Option>(timeOptions[38]);
  const [primaryTz, setPrimaryTz] = useState<Option | null>(
    primaryTimezoneOptions[0] ?? null,
  );
  const [useHolidays, setUseHolidays] = useState(true);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [scheduleWarningAccepted, setScheduleWarningAccepted] = useState(false);

  const toggleDay = (dayValue: number) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((v) => v !== dayValue)
        : [...prev, dayValue],
    );
  };

  const value: ContactWindowContextValue = useMemo(
    () => ({
      label,
      description,
      setLabel,
      setDescription,
      selectedDays,
      toggleDay,
      setSelectedDays,
      startTime,
      endTime,
      setStartTime,
      setEndTime,
      primaryTz,
      setPrimaryTz,
      useHolidays,
      setUseHolidays,
      blackoutDates,
      setBlackoutDates,
      scheduleWarningAccepted,
      setScheduleWarningAccepted,
    }),
    [
      label,
      description,
      selectedDays,
      startTime,
      endTime,
      primaryTz,
      useHolidays,
      blackoutDates,
      scheduleWarningAccepted,
    ],
  );

  return (
    <ContactWindowContext.Provider value={value}>
      {children}
    </ContactWindowContext.Provider>
  );
};

type UseContactWindowReturn = ContactWindowContextValue & {
  selectedDaysText: string;
  save: () => Promise<string | null>;
  isInitializing: boolean;
  timezoneError: string | null;
  showScheduleWarning: boolean;
};

export const useContactWindow = (): UseContactWindowReturn => {
  const ctx = useContext(ContactWindowContext);
  if (!ctx) {
    throw new Error(
      'useContactWindow must be used within ContactWindowProvider',
    );
  }

  const {
    setLabel: setCtxLabel,
    setDescription: setCtxDescription,
    setSelectedDays: setCtxSelectedDays,
    setStartTime: setCtxStartTime,
    setEndTime: setCtxEndTime,
    setPrimaryTz: setCtxPrimaryTz,
    setUseHolidays: setCtxUseHolidays,
    setBlackoutDates: setCtxBlackoutDates,
  } = ctx;

  const [contactWindowId, setContactWindowId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  const { get, post, patch } = useAuthedRequest();
  const notification = useNotification();
  const location = useLocation();

  const toTimeLabel = (value: string) => formatTimeLabel(value);

  const applyStartTime = (record: WorkflowContactWindow) => {
    if (record.startTime) {
      const found = timeOptions.find((o) => o.value === record.startTime);
      const option: Option = found || {
        label: toTimeLabel(record.startTime),
        value: record.startTime,
      };
      setCtxStartTime(option);
    }
  };

  const applyEndTime = (record: WorkflowContactWindow) => {
    if (record.endTime) {
      const found = timeOptions.find((o) => o.value === record.endTime);
      const option: Option = found || {
        label: toTimeLabel(record.endTime),
        value: record.endTime,
      };
      setCtxEndTime(option);
    }
  };

  const applyPrimaryTz = (record: WorkflowContactWindow) => {
    if (!record.primaryTz) return;

    // Legacy: contact-local is no longer supported by the UI.
    // If present, initialize primary from fallback (if available).
    if (record.primaryTz === WorkflowTimezone.CONTACT_LOCAL) {
      if (record.fallbackTz) {
        const found = primaryTimezoneOptions.find(
          (o) => o.value === record.fallbackTz,
        );
        const option: Option = found || {
          label: record.fallbackTz,
          value: record.fallbackTz,
        };
        setCtxPrimaryTz(option);
      } else {
        setTimezoneError(
          'This schedule uses a deprecated timezone setting. Please choose a primary timezone to continue.',
        );
        setCtxPrimaryTz(null);
      }
      return;
    }

    const found = primaryTimezoneOptions.find(
      (o) => o.value === record.primaryTz,
    );
    const option: Option = found || {
      label: record.primaryTz,
      value: record.primaryTz,
    };
    setCtxPrimaryTz(option);
  };

  useEffect(() => {
    (async () => {
      try {
        setTimezoneError(null);
        const params = new URLSearchParams(location.search);
        const requestedId = params.get('id');
        if (!requestedId) {
          return;
        }

        const selectedRecord = await get(
          `/workflow_contact_windows/${requestedId}`,
        );

        setContactWindowId(selectedRecord.id);
        setCtxLabel(selectedRecord.label);
        setCtxDescription(selectedRecord.description);
        setCtxSelectedDays(Array.from(new Set(selectedRecord.days)));
        applyStartTime(selectedRecord);
        applyEndTime(selectedRecord);
        applyPrimaryTz(selectedRecord);
        setCtxUseHolidays(selectedRecord.usHolidays);
        setCtxBlackoutDates(selectedRecord.blackoutDates);
      } catch {
        // No-op; notification on save
      } finally {
        setIsInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable across renders
  }, [location.search]);

  const selectedDaysText = useMemo(() => {
    const labels = ['Sun', 'Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat'];
    return ctx.selectedDays.map((d) => labels[d]).join(', ');
  }, [ctx.selectedDays]);

  const showScheduleWarning = useMemo(
    () =>
      isOutsideLegalHours(
        ctx.startTime.value,
        ctx.endTime.value,
        ctx.selectedDays,
      ),
    [ctx.startTime.value, ctx.endTime.value, ctx.selectedDays],
  );

  // Reset warning acceptance when schedule changes
  useEffect(() => {
    ctx.setScheduleWarningAccepted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when schedule values change
  }, [ctx.startTime.value, ctx.endTime.value, ctx.selectedDays]);

  const setPrimaryTz = useCallback(
    (option: Option | null) => {
      setTimezoneError(null);
      setCtxPrimaryTz(option);
    },
    [setCtxPrimaryTz],
  );

  const save = useCallback(async () => {
    try {
      if (timezoneError) {
        notification.error(timezoneError);
        return null;
      }
      if (!ctx.primaryTz) {
        notification.error('Please select a primary timezone');
        return null;
      }
      if (ctx.primaryTz.value === WorkflowTimezone.CONTACT_LOCAL) {
        notification.error('Please select a valid primary timezone');
        return null;
      }
      if (ctx.selectedDays.length === 0) {
        notification.error('At least one day must be selected for outreach');
        return null;
      }

      const toMinutes = (hhmm: string) => {
        const [hh, mm] = hhmm.split(':').map(Number);
        return hh * 60 + mm;
      };
      if (toMinutes(ctx.startTime.value) >= toMinutes(ctx.endTime.value)) {
        notification.error('Outreach start time must be before end time');
        return null;
      }
      if (showScheduleWarning && !ctx.scheduleWarningAccepted) {
        notification.error(
          'To save, check the box to confirm you’re placing calls outside legal hours.',
        );
        return null;
      }

      const body = {
        label: ctx.label,
        description: ctx.description,
        days: ctx.selectedDays,
        startTime: ctx.startTime.value,
        endTime: ctx.endTime.value,
        primaryTz: ctx.primaryTz.value,
        fallbackTz: null,
        usHolidays: ctx.useHolidays,
        blackoutDates: ctx.blackoutDates,
      };

      let nextContactWindowId = contactWindowId;
      if (contactWindowId) {
        await patch(`/workflow_contact_windows/${contactWindowId}`, body);
      } else {
        const created = await post(`/workflow_contact_windows`, body);
        if (!created?.id) {
          notification.error('Failed to update campaign schedule');
          return null;
        }
        nextContactWindowId = created.id;
        setContactWindowId(created.id);
      }
      notification.success('Campaign schedule updated');
      return nextContactWindowId;
    } catch {
      notification.error('Failed to update campaign schedule');
      return null;
    }
  }, [
    contactWindowId,
    ctx.label,
    ctx.description,
    ctx.selectedDays,
    ctx.startTime.value,
    ctx.endTime.value,
    ctx.primaryTz,
    ctx.useHolidays,
    ctx.blackoutDates,
    ctx.scheduleWarningAccepted,
    patch,
    post,
    notification,
    timezoneError,
    showScheduleWarning,
  ]);

  return {
    ...ctx,
    setPrimaryTz,
    selectedDaysText,
    save,
    isInitializing,
    timezoneError,
    showScheduleWarning,
  };
};
